/**
 * AI PLC Coder 主容器组件
 * 
 * 功能：
 * - 左右分栏布局（AI 对话 + 代码编辑器）
 * - 可拖拽调整分栏宽度
 * - AI 对话与代码编辑器的双向同步
 * - 响应式适配
 */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo
} from 'react';

import { AiChatPage } from '../../AIChatbotPlugin/pages/AiChatPage';
import { PlcCodeEditor, PlcCodeEditorRef } from './PlcCodeEditor';
import { useCodeSync } from '../hooks/useCodeSync';
import { useDiffReview } from '../hooks/useDiffReview';
import { useCodeSelection } from '../hooks/useCodeSelection';
import { usePlcCodeGeneration } from '../hooks/usePlcCodeGeneration';
import type { AIPlcCoderProps, PlcLanguageType, ParsedCodeBlock, CodeSelection } from '../types';
import '../styles/plcCoder.css';
import '../../AIChatbotPlugin/styles/index.css';

// ============================================
// 常量定义
// ============================================

/** 分栏最小宽度（像素） */
const MIN_PANEL_WIDTH = 320;

/** 分栏默认比例 */
const DEFAULT_SPLIT_RATIO = 0.45;

/** 拖拽状态 */
interface DragState {
  readonly isDragging: boolean;
  readonly startX: number;
  readonly startRatio: number;
}

// ============================================
// 代码检测提示组件
// ============================================

interface CodeDetectedToastProps {
  readonly block: ParsedCodeBlock;
  readonly onApply: () => void;
  readonly onDismiss: () => void;
}

const CodeDetectedToast: React.FC<CodeDetectedToastProps> = ({
  block,
  onApply,
  onDismiss
}) => {
  const langName = useMemo(() => {
    const names: Record<string, string> = {
      st: 'ST',
      il: 'IL',
      lad: 'LAD',
      fbd: 'FBD',
      unknown: '代码'
    };
    return names[block.language] ?? '代码';
  }, [block.language]);

  return (
    <div className="plc-code-detected-toast">
      <i className="fa fa-code plc-toast-icon" />
      <span className="plc-toast-text">
        检测到 {langName} 代码（{block.content.length} 字符）
      </span>
      <button
        type="button"
        className="plc-toast-btn"
        onClick={onApply}
      >
        应用到编辑器
      </button>
      <button
        type="button"
        className="plc-toast-close"
        onClick={onDismiss}
        aria-label="关闭"
      >
        <i className="fa fa-times" />
      </button>
    </div>
  );
};

// ============================================
// 主组件
// ============================================

export const AIPlcCoder: React.FC<AIPlcCoderProps> = ({
  businessType = 'plc-coder',
  businessId,
  defaultCapabilityCode,
  initialCode = '',
  initialLanguage = 'st',
  onCodeChange,
  className = ''
}) => {
  // ========================================
  // Refs
  // ========================================
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<PlcCodeEditorRef>(null);

  // ========================================
  // 分栏拖拽状态
  // ========================================
  const [splitRatio, setSplitRatio] = useState<number>(DEFAULT_SPLIT_RATIO);
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startRatio: DEFAULT_SPLIT_RATIO
  });
  const [isDragging, setIsDragging] = useState(false);

  // ========================================
  // 代码同步 Hook，使用 diff 策略
  // ========================================
  const {
    code,
    language,
    isDirty,
    detectedCodeBlock,
    setCode,
    setLanguage,
    processAIResponse,
    applyDetectedCode,
    dismissDetectedCode,
    generateAIContext
  } = useCodeSync({
    initialCode,
    initialLanguage,
    autoSyncFromAI: true,
    syncStrategy: 'diff',   // ✨ 使用 diff 审查策略
    onCodeChange
  });

  // ========================================
  // 代码生成 Hook
  // ========================================
  const {
    isGenerating,
    streamContent
  } = usePlcCodeGeneration({
    onCodeBlockReceived: (block) => {
      // 实时更新编辑器内容
      if (block.content) {
        setCode(block.content);
        if (block.language !== 'unknown') {
          setLanguage(block.language);
        }
      }
    }
  });

  // ========================================
  // 插入代码回调：追加到编辑器末尾（不覆盖）
  // ========================================
  const handleInsertCode = useCallback((insertedCode: string, _language: string): void => {
    if (!code.trim()) {
      setCode(insertedCode);
      return;
    }
    // 确保原有代码和新代码之间有空行分隔
    const separator = code.endsWith('\n') ? '\n' : '\n\n';
    setCode(code + separator + insertedCode);
  }, [code, setCode]);

  // ========================================
  // Diff 审查 Hook ✨ 新增
  // ========================================
  const {
    reviewState: diffReviewState,
    isReviewing: isDiffReviewing,
    startReview,
    acceptChanges,
    rejectChanges
  } = useDiffReview({
    onAccept: (newCode, newLanguage) => {
      // 接受：将新代码写入编辑器
      setCode(newCode);
      setLanguage(newLanguage);
    },
    onReject: () => {
      // 拒绝：不做任何事，保留旧代码
    }
  });

  // ========================================
  // 代码选区 Hook ✨ 新增
  // ========================================
  const {
    selection: codeSelection,
    setSelection: setCodeSelection,
    clearSelection: clearCodeSelection,
    buildPromptWithCode,
    hasSelection
  } = useCodeSelection();

  // ========================================
  // 新建对话回调：清空编辑器状态
  // ========================================
  const handleNewChat = useCallback((): void => {
    setCode(initialCode);
    setLanguage(initialLanguage);
    clearCodeSelection();
    dismissDetectedCode();
    // 如果处于 diff 审查中，拒绝变更以退出 diff 模式
    if (isDiffReviewing) {
      rejectChanges();
    }
  }, [initialCode, initialLanguage, setCode, setLanguage, clearCodeSelection, dismissDetectedCode, isDiffReviewing, rejectChanges]);

  // ========================================
  // 监听 detectedCodeBlock → 进入 diff 审查 ✨ 新增
  // ========================================
  useEffect(() => {
    if (detectedCodeBlock && detectedCodeBlock.content.trim()) {
      const targetLanguage =
        detectedCodeBlock.language !== 'unknown'
          ? detectedCodeBlock.language
          : language;

      startReview(code, detectedCodeBlock.content, targetLanguage);
      dismissDetectedCode(); // 清除 detectedCodeBlock，避免重复触发
    }
  }, [detectedCodeBlock, code, language, startReview, dismissDetectedCode]);

  // ========================================
  // 拖拽处理
  // ========================================
  const handleDragStart = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
    dragStateRef.current = {
      isDragging: true,
      startX: e.clientX,
      startRatio: splitRatio
    };
    setIsDragging(true);
  }, [splitRatio]);

  const handleDragMove = useCallback((e: MouseEvent): void => {
    if (!dragStateRef.current.isDragging || !containerRef.current) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const deltaX = e.clientX - dragStateRef.current.startX;
    const deltaRatio = deltaX / containerWidth;
    
    let newRatio = dragStateRef.current.startRatio + deltaRatio;
    
    // 限制最小/最大比例
    const minRatio = MIN_PANEL_WIDTH / containerWidth;
    const maxRatio = 1 - minRatio;
    newRatio = Math.max(minRatio, Math.min(maxRatio, newRatio));
    
    setSplitRatio(newRatio);
  }, []);

  const handleDragEnd = useCallback((): void => {
    dragStateRef.current = {
      ...dragStateRef.current,
      isDragging: false
    };
    setIsDragging(false);
  }, []);

  // 绑定全局拖拽事件
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // ========================================
  // AI 对话回调
  // ========================================
  
  /** 处理 AI 响应消息 */
  const handleAIMessage = useCallback((message: string): void => {
    // 解析 AI 响应中的代码块
    processAIResponse(message);
  }, [processAIResponse]);

  /** 处理发送代码到 AI 的事件 */
  useEffect(() => {
    const handleSendToAI = (): void => {
      const context = generateAIContext();
      if (context) {
        // 触发 AI 输入框填充事件
        const event = new CustomEvent('plc-fill-ai-input', {
          detail: { text: context }
        });
        window.dispatchEvent(event);
      }
    };

    window.addEventListener('plc-send-to-ai', handleSendToAI);
    return () => {
      window.removeEventListener('plc-send-to-ai', handleSendToAI);
    };
  }, [generateAIContext]);

  // ========================================
  // 编辑器选区回调 ✨ 新增
  // ========================================
  const handleSelectionChange = useCallback((sel: CodeSelection | null): void => {
    setCodeSelection(sel);
  }, [setCodeSelection]);

  // ========================================
  // 发送消息拦截：拼接选中代码 ✨ 新增
  // ========================================
  const handleBeforeSend = useCallback((text: string): string => {
    // ✨ 修复：buildPromptWithCode 内部通过 ref 读取最新选区，
    //    所以即使这里的闭包不包含最新 hasSelection，也没关系。
    //    直接调用 buildPromptWithCode，它会判断是否有选区。
    const combinedText = buildPromptWithCode(text);

    // 如果拼接后文本与原始文本不同，说明有选区被拼接了，清除选区
    if (combinedText !== text) {
      clearCodeSelection();
    }

    return combinedText;
  }, [buildPromptWithCode, clearCodeSelection]);

  // ========================================
  // 编辑器回调
  // ========================================
  
  /** 代码变更 */
  const handleCodeChange = useCallback((newCode: string): void => {
    setCode(newCode);
  }, [setCode]);

  /** 语言变更 */
  const handleLanguageChange = useCallback((newLang: PlcLanguageType): void => {
    setLanguage(newLang);
  }, [setLanguage]);

  // ========================================
  // 计算分栏样式
  // ========================================
  const panelStyles = useMemo(() => ({
    chat: {
      flex: `0 0 ${splitRatio * 100}%`,
      maxWidth: `${splitRatio * 100}%`
    },
    editor: {
      flex: `0 0 ${(1 - splitRatio) * 100}%`,
      maxWidth: `${(1 - splitRatio) * 100}%`
    }
  }), [splitRatio]);

  // ========================================
  // 渲染
  // ========================================
  return (
    <div
      ref={containerRef}
      className={`plc-coder-root ${className}`}
    >
      {/* 左侧：AI 对话区域 */}
      <div
        className="plc-coder-chat"
        style={panelStyles.chat}
      >
        <AiChatPage
          businessType={businessType}
          businessId={businessId}
          onMessageReceived={handleAIMessage}
          sidebarMode="drawer"           // 使用抽屉模式
          defaultCapabilityCode={defaultCapabilityCode}
          capabilityDisabled={true}     // 禁用能力选择
          showContextResources={false}   // 不显示上下文资源
          codeSelection={codeSelection}
          onClearCodeSelection={clearCodeSelection}
          onBeforeSend={handleBeforeSend}
          onClickNewChat={handleNewChat}
          onInsertCode={handleInsertCode}
          showInsertCodeBtn={true}
        />
      </div>

      {/* 可拖拽分隔条 */}
      <div
        className={`plc-coder-divider ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleDragStart}
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(splitRatio * 100)}
        aria-valuemin={10}
        aria-valuemax={90}
      />

      {/* 右侧：代码编辑器区域 */}
      <div
        className="plc-coder-editor"
        style={panelStyles.editor}
      >
        <PlcCodeEditor
          ref={editorRef}
          value={code}
          onChange={handleCodeChange}
          language={language}
          onLanguageChange={handleLanguageChange}
          loading={isGenerating}
          showToolbar
          onSelectionChange={handleSelectionChange}
          diffReviewState={isDiffReviewing ? diffReviewState : null}
          onAcceptChanges={acceptChanges}
          onRejectChanges={rejectChanges}
        />
      </div>

      {/* 代码检测提示（手动确认模式时显示） */}
      {/* {detectedCodeBlock && (
        <CodeDetectedToast
          block={detectedCodeBlock}
          onApply={applyDetectedCode}
          onDismiss={dismissDetectedCode}
        />
      )} */}
    </div>
  );
};

AIPlcCoder.displayName = 'AIPlcCoder';

export default AIPlcCoder;